import {
  Box,
  Card,
  CircularProgress,
  Typography,
  Avatar,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import apiService from "../../../../../../service/Api";
import { toast } from "react-toastify";
import { showErrorToast } from "../../../../../../components/common/ShowToast";
import { MemoryBook } from "../../../../../../types/memory-table";
import { motion } from "framer-motion";
import DownloadIcon from "@mui/icons-material/Download";
// import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
// import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { CurrentMemoryBookInfo } from "../../../memory-table/components/CurrentMemoryBookInfo";
import { IStreamDetails } from "../types/memorial-types";


interface IDownloadsTabProps {
  streamId: string;
  vimeoLink?: string;
  vimeoPassword?: string;
  currentStream?: IStreamDetails;
}

export const DownloadsTab = ({
  streamId,
  vimeoLink,
  vimeoPassword: _vimeoPassword,
  currentStream,
}: IDownloadsTabProps) => {
  void _vimeoPassword;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const deceased = currentStream?.booking?.deceased?.[0];

  const deceasedName = [deceased?.first_name, deceased?.surname]
    .filter(Boolean)
    .join(" ");

  const deceasedImage = deceased?.person_image;

  const [memoryBooks, setMemoryBooks] = useState<MemoryBook[]>([]);
  const [deliveredBookAvailable, setDeliveredBookAvailable] =
    useState<boolean>(false);

  const fetchMemoryBook = useCallback(async () => {
    setIsLoading(true);
    setError(false);

    try {
      const response = await apiService.get<{ results: MemoryBook[] }>(
        `/api/streams/memory-book/?stream=${streamId}`
      );
      console.log(response.data);

      const isDeveliveredBookAvailable = response.data.results.some(
        (memoryBook) => memoryBook.is_delivered
      );
      setDeliveredBookAvailable(isDeveliveredBookAvailable);
      setMemoryBooks(response.data.results);
    } catch (err: unknown) {
      console.error("Failed to load memory book:", err);
      toast.error("Failed to load comments. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [streamId]);

  useEffect(() => {
    fetchMemoryBook();
  }, [fetchMemoryBook]);
  const downloadCurrentVimeoLink = () => {
    try {
      if (!vimeoLink) {
        showErrorToast("No download link available.");
        return;
      }

      const a = document.createElement("a");
      a.href = vimeoLink;
      a.download = `memory_book_${streamId}.mp4`; // filename hint
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error: unknown) {
      console.error("Failed to download memory book:", error);
      showErrorToast("Failed to download memory book. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card
          sx={{
            height: "100%",
            boxShadow: 0,
            borderRadius: "20px",
            padding: "1.2rem",
          }}
        >
          <CircularProgress
            size={25}
            sx={{ display: "block", marginInline: "auto" }}
          />
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card
        sx={{
          height: "100%",
          boxShadow: 0,
          borderRadius: "20px",
          padding: "1.2rem",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box
          onClick={downloadCurrentVimeoLink}
          padding={2.5}
          borderRadius={4}
          mb={2}
          display="flex"
          flexDirection="column" // stack avatar+name row and download link vertically
          alignItems="flex-start"
          gap={1.5} // spacing between name row and download link
          sx={{
            cursor: "pointer",
            background: "linear-gradient(135deg, #FFFFFF, #F5F7FA)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            transition: "transform 0.25s, box-shadow 0.25s, background 0.25s",
            "&:hover": {
              transform: "translateY(-3px)",
              boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
              background: "linear-gradient(135deg, #F0F4F8, #E8EDF2)",
            },
          }}
        >
          {/* Avatar + Name Row */}
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              src={
                deceasedImage ||
                "https://static.vecteezy.com/system/resources/previews/002/318/271/original/user-profile-icon-free-vector.jpg"
              }
              alt={deceasedName || "Memorial Avatar"}
              sx={{
                width: 60,
                height: 60,
                border: "2px solid #1976d2",
              }}
            />

            <Typography
              variant="subtitle1"
              fontWeight={700}
              color="text.primary"
            >
              {deceasedName || "—"}
            </Typography>
          </Box>

          {/* Download Link below */}
          <Box display="flex" alignItems="center" gap={0.5} mt={0} ml={1}>
            <DownloadIcon color="primary" fontSize="small" />
            <Typography
              variant="body2"
              color="primary.main"
              sx={{
                textDecoration: "underline",
                fontWeight: 500,
                "&:hover": { color: "primary.dark" },
              }}
            >
              Open and download vimeo stream
            </Typography>
          </Box>
        </Box>

        {/* MemoryBook Section */}
        {deliveredBookAvailable ? (
          <CurrentMemoryBookInfo
            memoryBooks={memoryBooks}
            error={error}
            uploadUser={null}
            isAdmin={false}
            isLoading={isLoading}
          />
        ) : !vimeoLink ? (
          // Only show "No downloads available" if there is no Vimeo link
          <Typography
            variant="body2"
            sx={{ padding: "1.2rem", textAlign: "center" }}
          >
            No downloads available at the moment
          </Typography>
        ) : null}
      </Card>
    </motion.div>
  );
};
